import { useEffect, useState } from "react"
import { logger } from "../App/Logger"
import { ShipApi } from "../Types"
import type { IConnectedCompanion } from "../Types/Generated/LogbookApp/ShipApi"
import type { DataStore } from "./Store"

export type CompanionConnectionsState =
  | { status: "loading" }
  | { status: "loaded"; companions: IConnectedCompanion[] }
  | { status: "error" };

export function useConnectedCompanions(store: DataStore, shipId: number, enabled: boolean, revision = 0): CompanionConnectionsState | undefined {
  const [snapshot, setSnapshot] = useState<{
    store: DataStore;
    shipId: number;
    revision: number;
    state: CompanionConnectionsState;
  }>();

  useEffect(() => {
    setSnapshot(undefined);
    if (!enabled) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const response = await new ShipApi(store.api()).ships.companions({ params: { shipId } }).call();
        if (active) setSnapshot({ store, shipId, revision, state: { status: "loaded", companions: response.companions } });
      } catch (error) {
        if (active) {
          logger.error("Failed to load connected companions", error);
          setSnapshot({ store, shipId, revision, state: { status: "error" } });
        }
      } finally {
        // Schedule after completion so a slow primary never accumulates requests.
        if (active) timer = setTimeout(load, 5_000);
      }
    };
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [store, shipId, enabled, revision]);

  if (!enabled) return undefined;
  return snapshot?.store === store && snapshot.shipId === shipId && snapshot.revision === revision ? snapshot.state : { status: "loading" };
}
